import { useId, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, Paperclip, PenLine, Send, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Entrada, Selecao } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, post, getTodos } from '../../nucleo/api';
import { dataHora, nomeDeArquivo } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { janelaAberta, tamanhoArquivo, type AnexoMensagem, type Conversa, type Resposta, type TemplateItem } from './tipos';
import { preencherVariaveis, requisitosDeEnvio } from '@malgoos/shared';

/** Tipos que o seletor de arquivo oferece (o servidor confere pelos bytes de qualquer jeito). */
const ACEITOS = 'image/jpeg,image/png,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.txt,.csv,audio/ogg,audio/mpeg,audio/mp4,video/mp4';

/** Texto digitado só com "/atalho" (sem espaço) abre as sugestões de respostas rápidas. */
function termoDoAtalho(texto: string): string | null {
  const m = /^\/(\S*)$/.exec(texto);
  return m ? m[1]!.toLowerCase() : null;
}

export function EnviarTemplate({ conversa, aberto, aoFechar }: { conversa: Conversa; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['conversas', 'templates', 'aprovados'], queryFn: () => getTodos<TemplateItem>('/conversas/templates', { status: 'aprovado' }), enabled: aberto });
  const [templateId, setTemplateId] = useState('');
  // U14 F4: valores por campo: `c:<variável>` (corpo), `h` (cabeçalho) e `b:<posição>` (botão com parâmetro).
  const [valores, setValores] = useState<Record<string, string>>({});
  const escolhido = q.data?.itens.find((x) => x.id === templateId) ?? q.data?.itens[0];
  const req = useMemo(() => (escolhido ? requisitosDeEnvio(escolhido.componentes) : null), [escolhido]);
  const campos = useMemo(() => {
    if (!req) return [];
    const lista: { chave: string; rotulo: string; max: number }[] = req.corpo.map((v, i) => ({
      chave: `c:${v}`,
      rotulo: req.formato === 'nomeado' ? t('enviarModelo.variavelNome', { nome: v }) : t('enviarTemplate.variavel', { n: i + 1 }),
      max: 200,
    }));
    if (req.cabecalho?.tipo === 'texto') lista.push({ chave: 'h', rotulo: t('enviarModelo.cabecalho'), max: 60 });
    else if (req.cabecalho) lista.push({ chave: 'h', rotulo: t('enviarModelo.cabecalhoMidia'), max: 2000 });
    for (const b of req.botoes) lista.push({ chave: `b:${b.indice}`, rotulo: b.tipo === 'link' ? t('enviarModelo.botaoLink', { n: b.indice + 1 }) : t('enviarModelo.botaoCodigo', { n: b.indice + 1 }), max: b.tipo === 'link' ? 200 : 15 });
    return lista;
  }, [req, t]);
  const valor = (k: string) => (valores[k] ?? '').trim();
  const completo = !!escolhido && campos.every((c) => valor(c.chave)) && (req?.cabecalho?.tipo === 'texto' || !req?.cabecalho || /^https:\/\/\S+$/.test(valor('h')));
  const enviar = useMutation({
    mutationFn: () => {
      const r = req!;
      const cabecalho = r.cabecalho ? (r.cabecalho.tipo === 'texto' ? { tipo: 'texto', variaveis: [valor('h')] } : { tipo: r.cabecalho.tipo, link: valor('h') }) : undefined;
      const botoes = r.botoes.map((b) => (b.tipo === 'link' ? { indice: b.indice, tipo: 'link', texto: valor(`b:${b.indice}`) } : { indice: b.indice, tipo: 'codigo', codigo: valor(`b:${b.indice}`) }));
      return post(`/conversas/conversas/${conversa.id}/templates`, {
        template_id: escolhido!.id,
        variaveis: r.corpo.map((v) => valor(`c:${v}`)),
        ...(cabecalho ? { cabecalho } : {}),
        ...(botoes.length ? { botoes } : {}),
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['conversas'] });
      toast.sucesso(t('enviarTemplate.sucesso'));
      setValores({});
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('enviarTemplate.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!completo} carregando={enviar.isPending} onClick={() => enviar.mutate()}>
            {t('acoes.enviar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('enviarTemplate.texto')}</p>
        {q.isPending ? (
          <p className="text-sm text-tinta-3">{tn('comum.carregando')}</p>
        ) : !q.data?.itens.length ? (
          <p className="text-sm text-tinta-2">{t('enviarTemplate.vazio')}</p>
        ) : (
          <>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
              {t('enviarTemplate.template')}
              <Selecao
                opcoes={q.data.itens.map((x) => ({ valor: x.id, rotulo: `${x.nome} (${x.idioma})` }))}
                value={escolhido?.id ?? ''}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  setValores({});
                }}
              />
            </label>
            {campos.map((c) => (
              <label key={`${escolhido?.id}-${c.chave}`} className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
                {c.rotulo}
                <Entrada value={valores[c.chave] ?? ''} maxLength={c.max} inputMode={c.chave === 'h' && req?.cabecalho?.tipo !== 'texto' ? 'url' : undefined} onChange={(e) => setValores((v) => ({ ...v, [c.chave]: e.target.value }))} />
              </label>
            ))}
            {escolhido && req && (
              <div>
                <p className="kicker mb-1">{t('enviarTemplate.previa')}</p>
                <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm whitespace-pre-wrap text-tinta-1">
                  {preencherVariaveis(escolhido.corpo, Object.fromEntries(req.corpo.map((v) => [v, valores[`c:${v}`]])))}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/** Fase 2 (W9, F1d): assinatura de e-mail da pessoa (Conversas > E-mail). */
function AssinaturaModal({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['conversas', 'assinatura'], queryFn: () => get<{ texto: string }>('/conversas/assinatura'), enabled: aberto });
  const [texto, setTexto] = useState<string | null>(null);
  const valor = texto ?? q.data?.texto ?? '';
  const salvar = useMutation({
    mutationFn: () => api<{ texto: string }>('PUT', '/conversas/assinatura', { texto: valor }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['conversas', 'assinatura'] });
      toast.sucesso(t('assinatura.sucesso'));
      setTexto(null);
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('assinatura.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('assinatura.texto')}</p>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('assinatura.campo')}
          <textarea
            rows={5}
            maxLength={2000}
            value={valor}
            onChange={(e) => setTexto(e.target.value)}
            className="w-full min-w-0 resize-y rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </label>
      </div>
    </Modal>
  );
}

/**
 * Campo de mensagem com respostas rápidas por /atalho, envio por Enter e botão de template (WhatsApp). Fase 2 (W9): anexo
 * (WhatsApp, um por mensagem; e-mail, até 25 MB somados), resposta de e-mail com assinatura (Enter quebra a linha,
 * Ctrl+Enter envia) e, fora da janela de 24 h do WhatsApp, o template no lugar do texto livre.
 */
export function Composer({ conversa, conectado, oficial = true }: { conversa: Conversa; conectado: boolean; oficial?: boolean }) {
  const { t, i18n } = useTranslation('conversas');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [indice, setIndice] = useState(0);
  // Lista fechada por Esc, Tab ou saída do campo, sem mexer no texto; digitar de novo reabre.
  const [fechada, setFechada] = useState(false);
  const [modalTemplate, setModalTemplate] = useState(false);
  const [modalAssinatura, setModalAssinatura] = useState(false);
  const [anexos, setAnexos] = useState<AnexoMensagem[]>([]);
  const idLista = useId();
  const campo = useRef<HTMLTextAreaElement>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const ehEmail = conversa.canal === 'email';
  // Trilha 4: o canal do Portal aceita um arquivo por mensagem (documento ou imagem), como o WhatsApp.
  const aceitaAnexo = conversa.canal !== 'instagram';
  const umPorMensagem = conversa.canal === 'whatsapp' || conversa.canal === 'portal' || conversa.canal === 'site';
  // Fase 2 (W30): janela de 24 h e templates são da API oficial; com Baileys ou whatsmeow o texto é livre.
  const janela = !oficial || janelaAberta(conversa);
  const templates = conversa.canal === 'whatsapp' && oficial;
  const respostas = useQuery({ queryKey: ['conversas', 'respostas'], queryFn: () => getTodos<Resposta>('/conversas/respostas-rapidas'), staleTime: 60_000 });
  const termo = termoDoAtalho(texto);
  const sugestoes = useMemo(() => (termo === null ? [] : (respostas.data?.itens ?? []).filter((r) => r.atalho.startsWith(termo)).slice(0, 6)), [termo, respostas.data]);
  const aberta = sugestoes.length > 0 && !fechada;
  const ativa = Math.min(indice, Math.max(0, sugestoes.length - 1));

  const enviar = useMutation({
    mutationFn: (corpo: string) => post(`/conversas/conversas/${conversa.id}/mensagens`, { texto: corpo, anexo_ids: anexos.map((a) => a.id) }),
    onSuccess: async () => {
      setTexto('');
      setAnexos([]);
      await qc.invalidateQueries({ queryKey: ['conversas'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const subir = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData();
      fd.append('arquivo', f);
      return api<AnexoMensagem>('POST', `/conversas/conversas/${conversa.id}/anexos`, fd);
    },
    onSuccess: (a) => setAnexos((lista) => (umPorMensagem ? [a] : [...lista, a])),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  function aplicar(r: Resposta) {
    setTexto(r.texto);
    setIndice(0);
    setFechada(false);
    campo.current?.focus();
  }
  function disparar() {
    const corpo = texto.trim();
    if ((!corpo && !anexos.length) || !conectado || enviar.isPending || subir.isPending) return;
    enviar.mutate(corpo);
  }

  if (!s.pode('conversas.enviar')) return null;
  if (!janela) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-borda p-3">
        <p className="min-w-0 flex-1 basis-64 text-sm text-tinta-2">{t('janela.fechada')}</p>
        <Botao variante="primario" icone={<FileText className="size-4" />} disabled={!conectado} onClick={() => setModalTemplate(true)}>
          {t('janela.enviarTemplate')}
        </Botao>
        <EnviarTemplate conversa={conversa} aberto={modalTemplate} aoFechar={() => setModalTemplate(false)} />
      </div>
    );
  }
  return (
    <div className="relative border-t border-borda p-3">
      {/* r8 visual M4: respostas rápidas no padrão da busca remota: o campo é a combobox (aria-expanded, aria-controls,
          aria-activedescendant) e a lista fica fora da ordem de Tab; a opção ativa tem contorno de foco (3:1). */}
      {aberta && (
        <ul id={idLista} role="listbox" tabIndex={-1} aria-label={t('composer.rapidas')} onMouseDown={(e) => e.preventDefault()} className="absolute right-3 bottom-full left-3 z-20 mb-1 max-h-56 overflow-y-auto rounded-lg border border-borda bg-sup-2 py-1 shadow-xl">
          {sugestoes.map((r, i) => (
            <li
              key={r.id}
              id={`${idLista}-${i}`}
              role="option"
              aria-selected={i === ativa}
              onClick={() => aplicar(r)}
              className={`flex w-full min-w-0 cursor-pointer gap-3 px-3 py-2 text-left text-sm hover:bg-sup-3 ${i === ativa ? 'bg-sup-3 outline-2 -outline-offset-2 outline-foco' : ''}`}
            >
              <span className="numero shrink-0 text-acento-forte">/{r.atalho}</span>
              <TextoCortado className="text-tinta-2">{r.texto}</TextoCortado>
            </li>
          ))}
        </ul>
      )}
      {(anexos.length > 0 || subir.isPending) && (
        <ul className="mb-2 flex flex-wrap gap-1.5" aria-live="polite">
          {anexos.map((a) => (
            <li key={a.id} className="flex max-w-full min-w-0 items-center gap-1.5 rounded-full border border-borda bg-sup-2 py-0.5 pr-1 pl-2.5 text-xs text-tinta-2">
              <TextoCortado className="min-w-0">{nomeDeArquivo(a.nome, conversa.parceiro_nome)}</TextoCortado>
              <span className="numero shrink-0 text-tinta-3">{tamanhoArquivo(a.tamanho, i18n.language)}</span>
              <button type="button" aria-label={t('anexos.remover', { nome: nomeDeArquivo(a.nome, conversa.parceiro_nome) })} onClick={() => setAnexos((l) => l.filter((x) => x.id !== a.id))} className="rounded-full p-0.5 text-tinta-3 hover:text-tinta-1">
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
          {subir.isPending && <li className="text-xs text-tinta-3">{t('anexos.enviando')}</li>}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <textarea
          ref={campo}
          rows={ehEmail ? 4 : 2}
          aria-label={ehEmail ? t('emailComposer.placeholder') : t('composer.placeholder')}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={aberta}
          aria-controls={aberta ? idLista : undefined}
          aria-activedescendant={aberta ? `${idLista}-${ativa}` : undefined}
          placeholder={conectado ? (ehEmail ? t('emailComposer.placeholder') : t('composer.placeholder')) : t('composer.desconectado')}
          disabled={!conectado}
          value={texto}
          maxLength={ehEmail ? 20000 : 4096}
          onChange={(e) => {
            setTexto(e.target.value);
            setIndice(0);
            setFechada(false);
          }}
          onBlur={() => setFechada(true)}
          onKeyDown={(e) => {
            if (aberta) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIndice((ativa + 1) % sugestoes.length);
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIndice((ativa - 1 + sugestoes.length) % sugestoes.length);
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                aplicar(sugestoes[ativa]!);
                return;
              }
              if (e.key === 'Escape') {
                // Esc fecha só a lista (o texto fica); não fecha o painel nem o diálogo de baixo.
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                setFechada(true);
                return;
              }
              // Tab e Shift+Tab saem do campo fechando a lista, sem aplicar a sugestão.
              if (e.key === 'Tab') {
                setFechada(true);
                return;
              }
            } else if (sugestoes.length && e.key === 'ArrowDown') {
              e.preventDefault();
              setFechada(false);
              return;
            }
            // E-mail: Enter quebra a linha e Ctrl (ou Cmd) com Enter envia; chat: Enter envia.
            const envia = ehEmail ? e.key === 'Enter' && (e.ctrlKey || e.metaKey) : e.key === 'Enter' && !e.shiftKey;
            if (envia && !e.nativeEvent.isComposing) {
              e.preventDefault();
              disparar();
            }
          }}
          className="max-h-40 min-h-12 w-full min-w-0 flex-1 resize-none rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco disabled:opacity-60"
        />
        {aceitaAnexo && (
          <>
            <input
              ref={seletor}
              type="file"
              accept={ACEITOS}
              className="hidden"
              tabIndex={-1}
              aria-hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) subir.mutate(f);
              }}
            />
            <Botao
              aria-label={t('anexos.anexar')}
              title={umPorMensagem ? `${t('anexos.anexar')}. ${t('anexos.umPorMensagem')}` : `${t('anexos.anexar')}. ${t('anexos.limite')}`}
              disabled={!conectado}
              ocupado={subir.isPending}
              icone={<Paperclip className="size-4" />}
              onClick={() => seletor.current?.click()}
            />
          </>
        )}
        {templates && <Botao aria-label={t('acoes.template')} title={t('acoes.template')} disabled={!conectado} icone={<FileText className="size-4" />} onClick={() => setModalTemplate(true)} />}
        {ehEmail && <Botao aria-label={t('emailComposer.assinatura')} title={t('emailComposer.assinatura')} icone={<PenLine className="size-4" />} onClick={() => setModalAssinatura(true)} />}
        <Botao variante="primario" aria-label={t('acoes.enviar')} disabled={!conectado || (!texto.trim() && !anexos.length)} carregando={enviar.isPending} icone={<Send className="size-4" />} onClick={disparar}>
          <span className="hidden sm:inline">{t('acoes.enviar')}</span>
        </Botao>
      </div>
      <p className="mt-1.5 text-xs text-tinta-3">
        {ehEmail ? t('emailComposer.dica') : t('composer.dica')}
        {templates && conversa.janela_expira_em ? ` · ${t('janela.expira', { quando: dataHora(conversa.janela_expira_em) })}` : ''}
      </p>
      {templates && <EnviarTemplate conversa={conversa} aberto={modalTemplate} aoFechar={() => setModalTemplate(false)} />}
      {ehEmail && <AssinaturaModal aberto={modalAssinatura} aoFechar={() => setModalAssinatura(false)} />}
    </div>
  );
}
